import { useEffect } from 'react';
import { CheckCircle2, AlertCircle, Info, AlertTriangle, X } from 'lucide-react';
import { useUiStore, type Notification } from '@/store/ui.store';
import { cn } from '@/lib/cn';

const icons = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
  warning: AlertTriangle,
} as const;

const accents = {
  success: 'text-green-500',
  error: 'text-red-500',
  info: 'text-sky-500',
  warning: 'text-amber-500',
} as const;

function ToastItem({ n }: { n: Notification }) {
  const dismiss = useUiStore((s) => s.dismiss);
  const Icon = icons[n.variant];

  useEffect(() => {
    const t = setTimeout(() => dismiss(n.id), 4500);
    return () => clearTimeout(t);
  }, [n.id, dismiss]);

  return (
    <div
      className={cn(
        'pointer-events-auto flex w-80 items-start gap-3 rounded-lg border border-surface-border bg-white p-4 shadow-lg ' +
          'animate-slide-in dark:border-slate-800 dark:bg-slate-900',
      )}
      role="status"
    >
      <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', accents[n.variant])} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{n.title}</p>
        {n.description && <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{n.description}</p>}
      </div>
      <button
        onClick={() => dismiss(n.id)}
        className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
        aria-label="Cerrar"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Contenedor global de toasts. Montar una vez en la raíz de la app. */
export function ToastProvider() {
  const notifications = useUiStore((s) => s.notifications);
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col gap-2">
      {notifications.map((n) => (
        <ToastItem key={n.id} n={n} />
      ))}
    </div>
  );
}
